/**
 * Sink-agnostic "this browser error is noise" predicates.
 *
 * ## Why this file exists
 *
 * We run TWO client error sinks and they used to disagree. `loggerClient.ts`
 * (Datadog) carries a long, hard-won `beforeSend` that drops adblocker
 * casualties, stale-deploy chunk failures, aborted fetches and Google Maps
 * self-inflicted throws. PostHog's `capture_exceptions: true` had NO
 * equivalent, so every one of those families landed in PostHog Error Tracking
 * looking like a live application fault.
 *
 * That is not a cosmetic difference — it produces WRONG CONCLUSIONS. On
 * 2026-09-06 a health sweep read `UET is not defined` off PostHog and reported
 * the Bing pixel as broken on the highest-volume paid landing page. It is not
 * broken: `bat.js` was blocked for one user, the snippet in
 * `constants/scripts.ts` already `typeof`-guards it, and Datadog had been
 * correctly dropping it since 2026-08-30. The only thing that was actually
 * broken was that the two sinks disagreed.
 *
 * So: the rules that depend on nothing but the error text live HERE, and both
 * sinks call them. Rules that need a sink-specific event shape (Datadog's
 * `event.origin` / `event.http.status_code` network-drop rule, its
 * `pixelHostnames` resource-URL matching) stay in `loggerClient.ts` — they have
 * no PostHog counterpart to share with.
 *
 * ## Adding a rule
 *
 * If the rule reads only the message/stack text, add it here and both sinks
 * inherit it. Keep every predicate ANCHORED — a bare "* is not defined" or a
 * loose `includes('map')` would swallow real SpotMe faults, which is the
 * failure mode that matters far more than a little extra noise.
 */
import { isInjectedReferenceError } from './isInjectedReferenceError'

/**
 * Stale-deploy chunk failure: the tab was opened before the current Vercel
 * deploy, so dynamic imports fetch chunks that now 404.
 *
 * Kept SEPARATE from `isKnownClientErrorNoise` because Datadog's handler does
 * more than drop it — it reloads the page once to pick up the new bundle. The
 * caller owns that side effect (`staleChunkReload.ts`); this is only the
 * detection half. The last three alternatives are the native `import()`
 * messages (Chrome/Edge, Safari, Firefox).
 */
export const isStaleChunkError = (haystack: string): boolean =>
  /Loading chunk \S+ failed|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
    haystack
  )

/**
 * Apollo aborts in-flight requests on route changes via AbortController, and
 * the rejected promise is forwarded to both sinks. ~900/day on Datadog alone.
 */
const isAbortNoise = (haystack: string): boolean =>
  /AbortError|signal is aborted|The user aborted/i.test(haystack)

/**
 * Bing UET bootstrap ran but `bat.js` never defined the `UET` global — a
 * content blocker or in-app WebView proxy answered with an empty/stubbed body,
 * so `onload` fired with nothing loaded.
 *
 * The snippet in `constants/scripts.ts` now guards this with
 * `typeof UET === "undefined"`, but already-open tabs and cached bundles keep
 * running the unguarded version for a while, so keep filtering.
 *
 * `UET` is the only pixel global we dereference bare (fbq/lintrk/uetq/oaiq
 * self-define; gtag is guarded) — hence the exact match rather than a generic
 * "* is not defined" rule, which would also swallow real ReferenceErrors from
 * our own bundle.
 */
const isBlockedUetError = (haystack: string): boolean =>
  /\bUET is not defined\b/.test(haystack)

/**
 * Bot/crawler User-Agent spoofing produces "Cannot convert a Symbol value to a
 * string" in obscure polyfill paths checking a spoofed property. Real browsers
 * never hit this. ~30/day.
 */
const isSpoofedSymbolError = (haystack: string): boolean =>
  haystack.includes('Cannot convert a Symbol value')

/**
 * Cross-origin script errors get sanitized by the browser to "Script error."
 * with no stack and no context, because the script lacks CORS headers.
 * Unactionable, ~35/day.
 *
 * Takes the candidate messages SEPARATELY rather than the joined haystack, and
 * anchors on the whole string: a genuine error that merely mentions the phrase
 * inside its stack must keep logging. Datadog's SDK rewrites a thrown non-Error
 * into `Uncaught "Script error."` on `event.message` while `event.error.message`
 * keeps the raw text, so both fields have to be tested — anchoring on one alone
 * let a real one through on 2026-07-29.
 */
export const isCrossOriginScriptError = (
  messages: readonly (string | undefined)[]
): boolean =>
  messages.some(
    (m) =>
      typeof m === 'string' &&
      /^(uncaught\s+")?script error\.?"?$/i.test(m.trim())
  )

/**
 * Google Maps SDK background noise — three unrelated families, all
 * user-environment rather than SpotMe faults, all gracefully degraded:
 *
 *  - **mapId config fetch** (~317/day): valid mapId, intermittent, 299/299
 *    Chrome. Adblock/privacy extensions blocking `mapConfigs:batchGet`,
 *    service-worker-cached error responses, cookie partitioning, or
 *    backgrounded-tab restoration races. Maps falls back to raster tiles.
 *  - **module loader** (`Could not load "onion"` / `"util"` / `"marker"`): the
 *    SDK failed to fetch one of its OWN submodules mid-load on a flaky mobile
 *    network. The stack lives entirely in maps.googleapis.com's main.js.
 *  - **half-initialized teardown** (`NotLoadingAPIFromGoogleMapsError`,
 *    `getRootNode`): the SDK decided it wasn't served from the canonical path
 *    (in-app browsers, proxies, content blockers rewrite the script tag) and
 *    left itself degraded; `AdvancedMarker` then unmounts and Maps' own
 *    marker.js derefs a node React already removed. Contained since 2026-08-13
 *    by the scoped boundary in `Map/MapWrapper.tsx` — it costs the map and
 *    nothing else. The throw is inside MINIFIED library code, so the variable
 *    name changes per build ('b.getRootNode' → 'a.getRootNode') and each rename
 *    mints a fresh fingerprint. Drop the family, not the fingerprints.
 *
 * Each arm is origin-anchored to maps.googleapis.com (or to a Maps-specific
 * message) so a genuine app-side DOM error that merely mentions `getRootNode`
 * keeps logging. No SpotMe code calls `getRootNode` — verified, the only repo
 * hits are the MapWrapper comments.
 */
const isGoogleMapsNoise = (haystack: string): boolean => {
  const isMapConfigFailure =
    /Unable to fetch configuration for mapId|ID de mapa v[aá]lido|marcadores avanzados/i.test(
      haystack
    ) || haystack.includes('maps.googleapis.com/maps/api/mapsjs/mapConfigs')

  const isModuleLoadFailure =
    /Could not load "[^"]+"/.test(haystack) &&
    haystack.includes('maps.googleapis.com')

  const isTeardownCrash =
    haystack.includes('NotLoadingAPIFromGoogleMapsError') ||
    (haystack.includes('getRootNode') &&
      haystack.includes('maps.googleapis.com'))

  return isMapConfigFailure || isModuleLoadFailure || isTeardownCrash
}

/**
 * iOS in-app browsers (Facebook, Instagram, TikTok…) inject their host app's
 * bridge script into every page they render; it talks to the native side
 * through `window.webkit.messageHandlers`. When that script runs in a context
 * where the app never wired the bridge up, the deref throws an unhandled
 * `TypeError` on OUR page. First seen 2026-09-06 from the Facebook iOS browser
 * (`FBAN/FBIOS`) on a PMax display landing.
 *
 * This is the iOS half of the family `isInjectedReferenceError` covers on
 * Android, and it needs a rule of its own because it defeats that one twice
 * over: the throw is a `TypeError`, not a `ReferenceError`, and `WKUserScript`
 * injection inherits the DOCUMENT's URL — so every frame reads
 * `@ https://www.spotme.mx/<path>:1:724`, never `@ <anonymous>`.
 *
 * Anchored on the bridge path rather than on a stack shape, deliberately.
 * "Every frame points at the document URL" would also match a real failure in
 * one of our own inline `next/script` snippets, which is precisely what the
 * `ReferenceError` restriction on the Android rule exists to prevent. And
 * unlike an injected function name — the handle that leaked twice already —
 * `webkit.messageHandlers` is a WebKit platform API, so it is stable across
 * host apps rather than per-app. It appears nowhere in this repo (verified).
 *
 * Matches the bare `webkit.messageHandlers` deref too: a script that reaches it
 * without the `window.` prefix reports the same property path.
 */
const isInjectedWebkitBridgeError = (haystack: string): boolean =>
  /\bwebkit\.messageHandlers\b/.test(haystack)

/**
 * The Android half of the bridge family above. Android in-app browsers
 * (Facebook `FB_IAB/FB4A`, etc.) expose their native side to injected scripts
 * through `addJavascriptInterface`; when the host app tears that Java object
 * down while the page is still alive (tab backgrounded, WebView recycled), the
 * next call from the injected script throws
 * `Error: Error invoking <method>: Java object is gone` with a one-line stack
 * and no frames of ours. Seen with `needInjectCss` and `postMessage` as the
 * method, which is why it is anchored on the platform message rather than the
 * per-app method name (the handle that leaked twice already).
 *
 * First paged 2026-10-16 on /anfitrion/valua-tu-propiedad from the Meta
 * supply_mty_c ad (Honor, Android 13, Facebook IAB). SpotMe ships no
 * `addJavascriptInterface` bridge — "Java object" appears nowhere in this repo.
 */
const isAndroidJsBridgeGoneError = (haystack: string): boolean =>
  /Error invoking \w+: Java object is gone/.test(haystack)

/**
 * WebKit tearing down IndexedDB underneath a live connection. Three distinct
 * messages, one root cause — iOS Safari reclaims or clears site storage while
 * the page still holds an open handle, and the next transaction on that dead
 * handle throws:
 *
 *   - "Connection to Indexed Database server lost" — storage evicted under
 *     memory pressure when WebKit suspends a backgrounded/idle tab.
 *   - "Database deleted by request of the user" — site data cleared
 *     mid-session, by the user or by WebKit's cap on script-writable storage.
 *   - "Object store cannot be found in the database" — the handle survives the
 *     teardown but the store it points at is gone. First seen 2026-09-12.
 *
 * The only IndexedDB consumer in this bundle is Firebase Auth's LOCAL
 * persistence (`firebaseLocalStorageDb`) — no service worker, no PWA cache, no
 * IDB of our own (verified). All three arrive with ZERO stack frames in BOTH
 * sinks, i.e. thrown from the SDK's async IDB internals, outside any `.catch()`
 * product code could own: the `setPersistence()` catch in
 * `firebase/firebaseClient.ts` wraps the initial call, not later transactions.
 * Firebase re-creates the store on the next load; the app keeps working.
 *
 * Each episode is ONE device retry-storming for seconds, so it also reads as a
 * volume incident: 416 events in 5.5 min from one iPad (2026-08-06), 10 in 4s
 * from one iPhone (2026-09-12). At 77% of all Datadog Error Tracking ingestion
 * over 30d (measured 2026-08-11) this family was most of that bill.
 *
 * Lives HERE rather than in `loggerClient.ts` (where the first message was
 * filtered alone from 2026-08-11) because PostHog never inherited it: on
 * 2026-09-12 all three were active in PostHog Error Tracking while Datadog had
 * been dropping the first for a month, and PostHog had auto-scored the newest
 * `severity: high`. That is the same two-sinks-disagree failure this file was
 * created to end.
 *
 * Anchored on the three exact messages rather than a loose /database/i, per the
 * file's rule: we own no IDB code today, but a future SpotMe fault that happens
 * to mention a "database" must stay visible.
 */
const isWebkitIdbTeardownError = (haystack: string): boolean =>
  /Connection to Indexed Database server lost|Database deleted by request of the user|Object store cannot be found in the database/i.test(
    haystack
  )

/**
 * The browser's ResizeObserver loop guard: an observer callback changed layout,
 * so the remaining notifications slip to the next frame. The spec reports it
 * as an error event with no stack, but nothing is lost — the observer just
 * fires again next frame. Every library that measures its own box (MUI
 * Autocomplete/Popper, Swiper, react-virtual) can trigger it.
 *
 * First paged 2026-10-07 on `/auth/action` (one Chrome session, no stack, no
 * user impact). Anchored on the two exact browser messages (Chrome/Edge
 * current + legacy) so a real fault that merely mentions ResizeObserver stays
 * visible.
 */
const isResizeObserverLoopNoise = (haystack: string): boolean =>
  /ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)/.test(
    haystack
  )

type NoiseInput = {
  /** Message + stack joined; the text every rule below matches against. */
  haystack: string
  /** Candidate message fields, tested individually by the anchored rules. */
  messages?: readonly (string | undefined)[]
  /** Sink-native error object, for the injected-script stack-shape check. */
  error?: { kind?: string; stack?: string }
}

/**
 * Our OWN deliberate throw, not a fault: `guardCapabilityNavigation`
 * (`capabilityPrivacy.ts`) cancels a client-side route change into or out of a
 * private token page by throwing after it has already started a full document
 * navigation — that is how Next's router is told to stand down. Reached both
 * sinks as an unhandled rejection (~11/week on `/auth/action` and
 * `/equipo/invitacion/[token]`, 2026-10-28 sweep). Exact message match, so an
 * unrelated error never hides behind it.
 */
const isCapabilityNavigationCancel = (haystack: string): boolean =>
  haystack.includes('Private page requires document navigation')

/**
 * The shared verdict: is this error a known, unactionable browser/third-party
 * artifact rather than a SpotMe fault?
 *
 * Deliberately EXCLUDES `isStaleChunkError` — that one carries a page-reload
 * side effect its caller must own — so a caller that wants chunk errors dropped
 * has to say so explicitly.
 */
export const isKnownClientErrorNoise = ({
  haystack,
  messages = [],
  error
}: NoiseInput): boolean =>
  isAbortNoise(haystack) ||
  isBlockedUetError(haystack) ||
  isSpoofedSymbolError(haystack) ||
  isGoogleMapsNoise(haystack) ||
  isInjectedWebkitBridgeError(haystack) ||
  isAndroidJsBridgeGoneError(haystack) ||
  isWebkitIdbTeardownError(haystack) ||
  isResizeObserverLoopNoise(haystack) ||
  isCapabilityNavigationCancel(haystack) ||
  isCrossOriginScriptError(messages) ||
  isInjectedReferenceError(error)
